import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Users, CalendarDays, TrendingUp, Wallet, Phone, Navigation, Plus, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { salesApi } from '../salesApi';
import { useT, fmtMoney, fmtDate } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';
import { StatusBadge, STATUS_ORDER, STATUS_STYLES, VisitTypeBadge } from '../components/StatusBadge';
import { LeadCard } from '../components/LeadCard';
import { EmptyState } from '../components/EmptyState';
import { FollowUpCard } from '../components/FollowUpCard';
import { CommissionCard } from '../components/CommissionCard';
import { GoalCard } from '../components/GoalCard';
import { BellRing } from 'lucide-react';

const mapsUrl = (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || '')}`;

const HERO = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

// Número do KPI: cabe sempre no card — reduz a fonte (até 15px) e, se ainda não couber, quebra a linha
const FitText = ({ children, className = '', testId }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      el.style.fontSize = '';
      el.style.whiteSpace = 'nowrap';
      let size = parseFloat(getComputedStyle(el).fontSize);
      while (size > 15 && el.scrollWidth > el.clientWidth + 0.5) { size -= 1; el.style.fontSize = `${size}px`; }
      if (el.scrollWidth > el.clientWidth + 0.5) el.style.whiteSpace = 'normal';
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);
  return <p ref={ref} className={`min-w-0 overflow-hidden break-words ${className}`} data-testid={testId}>{children}</p>;
};

const Kpi = ({ icon: Icon, label, value, sub, testId, delay = '', accent = '' }) => {
  const iconCls = `rounded-2xl border flex items-center justify-center shrink-0 ${accent === 'amber' ? 'bg-amber-400 text-white border-amber-300' : 'bg-teal-50 text-teal-700 border-teal-100'}`;
  return (
    <div data-testid={testId} className={`min-w-0 overflow-hidden backdrop-blur-xl border rounded-3xl p-4 sm:p-5 shadow-sm sales-card-hover sales-rise ${delay} ${accent === 'amber' ? 'bg-amber-50/80 border-amber-200/80' : 'bg-white/75 border-white/80'}`}>
      <div className="flex items-start justify-between gap-2 min-w-0">
        <FitText className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 tabular-nums leading-none" testId={`${testId}-value`}>{value}</FitText>
        <span className={`hidden sm:flex h-9 w-9 ${iconCls}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="flex items-end justify-between gap-2 mt-3 min-w-0">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-600 break-words">{label}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5 break-words">{sub}</p>}
        </div>
        <span className={`sm:hidden h-8 w-8 ${iconCls}`}><Icon className="h-4 w-4" /></span>
      </div>
    </div>
  );
};

const VisitRow = ({ v, lang, t, showDate }) => (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-200/60 last:border-0" data-testid={`dashboard-visit-${v.id}`}>
    <div className="text-sm font-semibold text-slate-700 w-14 shrink-0 tabular-nums">{showDate ? fmtDate(v.date, lang, { day: '2-digit', month: 'short' }) : v.time}</div>
    <div className="min-w-0 flex-1">
      <Link to={`/leads/${v.lead_id}`} className="font-semibold text-sm hover:text-primary truncate block">{v.lead_name}</Link>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><VisitTypeBadge type={v.type} />{showDate && <span className="tabular-nums">{v.time}</span>}<span className="truncate">{v.lead_address}</span></div>
    </div>
    <div className="flex gap-1 shrink-0">
      {v.lead_phone && <a href={`tel:${v.lead_phone}`} className="h-9 w-9 rounded-full border border-teal-200 bg-white/70 text-teal-700 flex items-center justify-center hover:bg-teal-50 transition-colors" aria-label={t.call}><Phone className="h-4 w-4" /></a>}
      {v.lead_address && <a href={mapsUrl(v.lead_address)} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-teal-200 bg-white/70 text-teal-700 flex items-center justify-center hover:bg-teal-50 transition-colors" aria-label={t.navigate}><Navigation className="h-4 w-4" /></a>}
    </div>
  </div>
);

export default function DashboardPage() {
  const { t, lang } = useT();
  const { user } = useSalesAuth();
  const { openNewLead } = useOutletContext();
  const [data, setData] = useState(null);

  useEffect(() => { salesApi.get('/dashboard').then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);

  if (!data) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">{t.loading}</div>;

  const counts = data.counts || {};
  const maxCount = Math.max(1, ...STATUS_ORDER.map((s) => counts[s] || 0));
  const followUps = data.follow_ups || [];

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="sales-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t.dashboard}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{t.hello}, {user?.full_name?.split(' ')[0]}</h1>
        </div>
        <Button onClick={openNewLead} className="h-11 font-semibold" data-testid="dashboard-new-lead-btn"><Plus className="h-4 w-4 mr-1.5" /> {t.newLead}</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Kpi icon={Users} label={t.openLeads} value={data.open_leads} sub={`${data.total_leads} total`} testId="kpi-open-leads" />
        <Kpi icon={Wallet} label={t.pipelineValue} value={fmtMoney(data.pipeline_value, lang)} testId="kpi-pipeline-value" delay="sales-rise-1" />
        <Kpi icon={TrendingUp} label={t.conversion} value={`${data.conversion_rate}%`} sub={`${counts.won || 0} ${t.status.won.toLowerCase()} · ${counts.lost || 0} ${t.status.lost.toLowerCase()}`} testId="kpi-conversion" delay="sales-rise-2" />
        <Kpi icon={CalendarDays} label={t.today} value={data.today_visits?.length || 0} sub={t.visits.toLowerCase()} testId="kpi-today-visits" delay="sales-rise-3" />
        <Kpi icon={BellRing} label={t.followUps} value={followUps.length} sub={t.followUpKpiSub(data.follow_up_days || 7)} testId="kpi-followups" delay="sales-rise-4" accent={followUps.length > 0 ? 'amber' : ''} />
      </div>

      <FollowUpCard items={followUps} days={data.follow_up_days} onChange={(next) => setData((d) => ({ ...d, follow_ups: next }))} />

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4 sm:gap-6">
        <div className="space-y-4 sm:space-y-6">
        <GoalCard isManager={user?.role === 'manager'} />
        <CommissionCard isManager={user?.role === 'manager'} />
        <section className="bg-card rounded-xl border border-border p-4 sm:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-base sm:text-lg">{t.today} · {fmtDate(data.today, lang, { weekday: 'long', day: '2-digit', month: 'long' })}</h2>
            <Link to="/agenda" className="text-xs font-semibold text-primary inline-flex items-center gap-1" data-testid="dashboard-agenda-link">{t.agenda} <ArrowRight className="h-3 w-3" /></Link>
          </div>
          {data.today_visits?.length ? data.today_visits.map((v) => <VisitRow key={v.id} v={v} lang={lang} t={t} />) : <p className="text-sm text-muted-foreground py-4" data-testid="dashboard-no-visits">{t.noVisitsToday}</p>}
          {data.upcoming_visits?.length > 0 && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-5 mb-1">{t.upcoming}</p>
              {data.upcoming_visits.slice(0, 5).map((v) => <VisitRow key={v.id} v={v} lang={lang} t={t} showDate />)}
            </>
          )}
        </section>
        </div>

        <section className="space-y-4 sm:space-y-6">
          <div className="hidden lg:block relative rounded-3xl overflow-hidden border border-white/80 shadow-sm h-44 sales-rise sales-rise-2" data-testid="dashboard-hero-card">
            <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 text-white">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">{t.simulation}</p>
              <p className="text-sm font-semibold mt-0.5">{lang === 'en' ? 'Show the before & after in seconds.' : 'Mostre o antes & depois em segundos.'}</p>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 sm:p-6 shadow-sm">
          <h2 className="font-display font-bold text-base sm:text-lg mb-4">{t.pipeline}</h2>
          <div className="space-y-3" data-testid="dashboard-pipeline">
            {STATUS_ORDER.map((s) => (
              <Link key={s} to={`/leads?status=${s}`} className="block group" data-testid={`pipeline-row-${s}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <StatusBadge status={s} testId={`pipeline-badge-${s}`} />
                  <span className="text-xs text-slate-600 tabular-nums"><b className="text-slate-900">{counts[s] || 0}</b> · {fmtMoney(data.values?.[s], lang)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-200/70 overflow-hidden">
                  <div className={`h-full rounded-full ${STATUS_STYLES[s].bar} transition-[width] duration-500`} style={{ width: `${((counts[s] || 0) / maxCount) * 100}%` }} />
                </div>
              </Link>
            ))}
          </div>
          </div>
        </section>
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-base sm:text-lg">{t.recentLeads}</h2>
          <Link to="/leads" className="text-xs font-semibold text-primary inline-flex items-center gap-1" data-testid="dashboard-leads-link">{t.seeAll} <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {data.recent_leads?.length ? (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.recent_leads.map((l) => <LeadCard key={l.id} lead={l} />)}
          </div>
        ) : (
          <EmptyState icon={Users} title={t.noLeads} hint={t.noLeadsHint} action={<Button onClick={openNewLead} className="h-11" data-testid="dashboard-empty-new-lead"><Plus className="h-4 w-4 mr-1.5" />{t.newLead}</Button>} testId="dashboard-empty-leads" />
        )}
      </section>
    </div>
  );
}
